'use client'
import * as Popover from '@radix-ui/react-popover'
import { Portal as RadixPortal } from '@radix-ui/react-portal'
import { useDomEvent, useWindowDomEvent } from 'hooks/use-dom-event'
import * as React from 'react'
import { callAll } from 'utils/call-all'
import { tw } from 'utils/classnames'

// eslint-disable-next-line no-barrel-files/no-barrel-files
// oxlint-disable-next-line cf-web/no-barrel-file
export * from '@radix-ui/react-popover'

const showSafeArea =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('showSafeArea')

type Behaviour = 'click' | 'hover' | 'both'
type FocusBehaviour = 'focus' | 'none'
type RootProps = Popover.PopoverProps & {
  id?: string
  behaviour?: Behaviour
  focusBehaviour?: FocusBehaviour
}

interface PopoverContextValue {
  open: boolean
  setOpen(value: boolean): void
  behaviour?: Behaviour
  focusBehaviour?: FocusBehaviour
  triggerRef?: React.RefObject<HTMLButtonElement | null>
  contentRef?: React.RefObject<HTMLDivElement | null>
  safeAreaCheckingRef?: React.RefObject<boolean | null>
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null)

export function usePopoverContext() {
  const context = React.useContext(PopoverContext)

  if (!context) {
    throw new Error(`usePopoverContext() must be called inside <Popover.Root />`)
  }

  return context
}

export function Portal(props: Popover.PopoverPortalProps) {
  return <Popover.Portal {...props} />
}

export function Root({ behaviour = 'click', focusBehaviour = 'focus', ...props }: RootProps) {
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const safeAreaCheckingRef = React.useRef(false)

  const isControlled = props.open !== undefined

  const context = React.useMemo(() => {
    if (isControlled) {
      return {
        open: props.open!,
        setOpen: props.onOpenChange!,
        behaviour,
        focusBehaviour,
        triggerRef,
        contentRef,
        safeAreaCheckingRef,
      }
    }

    return {
      open,
      setOpen,
      behaviour,
      focusBehaviour,
      triggerRef,
      contentRef,
      safeAreaCheckingRef,
    }
  }, [behaviour, focusBehaviour, isControlled, open, props.onOpenChange, props.open])

  const rootProps = React.useMemo(() => {
    if (isControlled) {
      return props
    }

    return {
      ...props,
      open,
      onOpenChange: setOpen,
    }
  }, [isControlled, open, props])

  const currentOpen = isControlled ? props.open : open

  React.useEffect(() => {
    if (!currentOpen) {
      safeAreaCheckingRef.current = false
    }
  }, [currentOpen])

  React.useEffect(() => {
    const observer = new ResizeObserver(() => {
      try {
        const isVisible = triggerRef.current?.checkVisibility()
        if (!isVisible) {
          setOpen(false)
        }
      } catch {}
    })

    triggerRef.current && observer.observe(triggerRef.current)

    return () => observer.disconnect()
  }, [])

  return (
    <PopoverContext.Provider value={context}>
      <Popover.Root {...rootProps} data-popover-root />
      {hasHoverBehaviour(behaviour) && currentOpen && (
        <SafeAreaOverlay
          triggerRef={triggerRef}
          contentRef={contentRef}
          safeAreaCheckingRef={safeAreaCheckingRef}
          onClose={() => {
            safeAreaCheckingRef.current = false
            ;(isControlled ? props.onOpenChange : setOpen)?.(false)
          }}
        />
      )}
    </PopoverContext.Provider>
  )
}

function TriggerRoot(
  props: Popover.PopoverTriggerProps & {
    ref?: React.Ref<HTMLButtonElement | null>
  },
) {
  const { ref, ...restProps } = props
  const ctx = usePopoverContext()
  const setOpen = ctx?.setOpen
  const open = ctx?.open
  const behaviour = ctx?.behaviour
  const focusBehaviour = ctx?.focusBehaviour
  const isKeyPress = React.useRef(false)
  const triggerRef = ctx?.triggerRef
  const contentRef = ctx?.contentRef
  const safeAreaCheckingRef = ctx?.safeAreaCheckingRef

  React.useEffect(() => {
    if (!open && focusBehaviour === 'none') {
      setTimeout(() => {
        triggerRef?.current?.blur()
      }, 1)
    }
  }, [open, focusBehaviour, triggerRef])

  React.useImperativeHandle<HTMLButtonElement | null, HTMLButtonElement | null>(
    ref,
    () => triggerRef?.current ?? null,
  )

  function onMouseLeave(e: React.MouseEvent) {
    if (contentRef?.current && isNode(e.target) && !contentRef.current.contains(e.target)) {
      if (hasHoverBehaviour(behaviour) && safeAreaCheckingRef) {
        safeAreaCheckingRef.current = true
      } else {
        setOpen?.(false)
      }
    }
  }

  return (
    <Popover.Trigger
      {...restProps}
      ref={triggerRef}
      onClick={e => {
        // prevent the mouse click from closing the popover when
        // in the 'hover' mode. otherwise a user can click on an
        // a trigger whilst it is open, and it will close the modal
        if (behaviour === 'hover' && !isKeyPress.current) {
          e.preventDefault()
        }
        isKeyPress.current = false
      }}
      data-popover-trigger
      onKeyDown={() => (isKeyPress.current = true)}
      onMouseEnter={callAll(
        hasHoverBehaviour(behaviour) &&
          (() => {
            setOpen?.(true)
            if (safeAreaCheckingRef) {
              safeAreaCheckingRef.current = false
            }
          }),
        hasHoverBehaviour(behaviour) &&
          focusBehaviour === 'focus' &&
          (() => triggerRef?.current?.focus()),
        restProps.onMouseEnter,
      )}
      onMouseLeave={callAll(hasHoverBehaviour(behaviour) && onMouseLeave, restProps.onMouseLeave)}
    />
  )
}

function hasHoverBehaviour(behaviour?: Behaviour) {
  return behaviour === 'hover' || behaviour === 'both'
}

function isNode(value: EventTarget | null): value is Node {
  return typeof Node !== 'undefined' && value instanceof Node
}

export const Trigger = TriggerRoot

// eslint-disable-next-line import/namespace
interface ContentRootProps extends Popover.PopoverContentProps {
  enableClickToClose?: boolean
  preventClickOutsideClose?: boolean
  ref?: React.Ref<HTMLDivElement | null>
}

const SIDE_HITBOX: Record<string, string> = {
  right:
    'after:absolute after:-left-[--hitbox-size] after:top-0 after:h-full after:w-[--hitbox-size] after:content-[""]',
  left: 'after:absolute after:-right-[--hitbox-size] after:top-0 after:h-full after:w-[--hitbox-size] after:content-[""]',
  bottom:
    'after:absolute after:-top-[--hitbox-size] after:left-0 after:h-[--hitbox-size] after:w-full after:content-[""]',
  top: 'after:absolute after:-bottom-[--hitbox-size] after:left-0 after:h-[--hitbox-size] after:w-full after:content-[""]',
}

function sideHitbox(side?: string) {
  return SIDE_HITBOX[side ?? 'bottom'] ?? null
}

// enableClickToClose is a workaround to make the popover stay open on very specific scenarios
// in almost every case you shouldn't need it
function ContentRoot({
  children,
  enableClickToClose = true,
  preventClickOutsideClose = false,
  ref,
  ...props
}: ContentRootProps) {
  const { contentRef, setOpen, triggerRef } = usePopoverContext()
  const behaviour = usePopoverContext()?.behaviour
  const safeAreaCheckingRef = usePopoverContext()?.safeAreaCheckingRef
  const isMouseDown = React.useRef(false)
  const isClickInside = React.useRef(false)

  useWindowDomEvent(
    'mousedown',
    React.useCallback(() => (isMouseDown.current = true), []),
  )

  useWindowDomEvent(
    'mouseup',
    React.useCallback(() => (isMouseDown.current = false), []),
  )

  useWindowDomEvent(
    'mousedown',
    React.useCallback(
      (e: Event) => {
        if (
          enableClickToClose &&
          isNode(e.target) &&
          contentRef?.current &&
          !contentRef.current.contains(e.target) &&
          triggerRef?.current &&
          !triggerRef.current.contains(e.target) &&
          preventClickOutsideClose &&
          !isClickInside.current
        ) {
          setOpen?.(false)
        }
        isClickInside.current = false
      },
      [enableClickToClose, contentRef, triggerRef, setOpen, preventClickOutsideClose],
    ),
  )

  React.useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
    ref,
    () => contentRef?.current ?? null,
  )

  const sideOffset = props.sideOffset || 0

  return (
    <Popover.Content
      side="bottom"
      // prevent autofocus on popovers for now since it gives them the outline
      onOpenAutoFocus={e => e.preventDefault()}
      {...props}
      ref={contentRef}
      onCloseAutoFocus={callAll(
        hasHoverBehaviour(behaviour) && ((e: Event) => e.preventDefault()),
        props.onCloseAutoFocus,
      )}
      onMouseEnter={callAll(
        hasHoverBehaviour(behaviour) &&
          (() => {
            if (!isMouseDown.current) {
              setOpen?.(true)
              if (safeAreaCheckingRef) {
                safeAreaCheckingRef.current = false
              }
            }
          }),
        props.onMouseEnter,
      )}
      onMouseLeave={callAll(
        hasHoverBehaviour(behaviour) &&
          ((e: React.MouseEvent) => {
            if (!isMouseDown.current) {
              const related = e.relatedTarget
              if (isNode(related) && triggerRef?.current?.contains(related)) {
                return
              }
              setOpen?.(false)
            }
          }),
        props.onMouseLeave,
      )}
      // the after pseudo element acts as a kind of hitbox, and we want the hitbox
      // to start from the top of the content, to the bottom of the trigger. we know this gap
      // from the sideOffset prop, so we set the height of the after element to that value
      style={{
        ...props.style,
        '--hitbox-size': sideOffset ? `${sideOffset + 1}px` : '0px', // +1 for good measure/safety
      }}
      data-popover-content
      onClickCapture={() => (isClickInside.current = true)}
      className={tw(
        // zindex on all
        'relative z-50',
        // no outline on popover contents
        'outline-none',
        // max height is max available space
        'max-h-[calc(var(--radix-popover-content-available-height)-8px)]',

        // hitbox css, only apply if there is a sideOffset
        sideOffset ? sideHitbox(props.side) : null,

        // ensure hitbox is above the content
        'after:z-10',
        props.className,
      )}
    >
      {children}
    </Popover.Content>
  )
}

export const Content = ContentRoot

// --- Mouse safe area ---
//
// An SVG polygon sits in a portal above triggers. While the popover is
// open and the mouse is on the trigger, the polygon follows the cursor
// as a visual triangle (pointer-events: none).
//
// When the mouse leaves the trigger, the triangle freezes in place and
// gets pointer-events: all — it physically becomes part of the safe
// area. The polygon's own mouseleave event decides what happens next:
// mouse went to content → stay open, mouse went to trigger → stay
// open, anything else → close.

function SafeAreaOverlay({
  triggerRef,
  contentRef,
  safeAreaCheckingRef,
  onClose,
}: {
  triggerRef: React.RefObject<HTMLButtonElement | null>
  contentRef: React.RefObject<HTMLDivElement | null>
  safeAreaCheckingRef: React.RefObject<boolean>
  onClose: () => void
}) {
  const polygonRef = React.useRef<SVGPolygonElement>(null)
  const onCloseRef = React.useRef(onClose)
  onCloseRef.current = onClose
  const wasCheckingRef = React.useRef(false)

  const handleMouseMove = React.useCallback(
    (evt: Event) => {
      const e = evt as MouseEvent
      const poly = polygonRef.current
      const content = contentRef.current
      if (!poly || !content) {
        return
      }

      const pos = { x: e.clientX, y: e.clientY }
      const rect = content.getBoundingClientRect()
      const inContent =
        pos.x >= rect.left && pos.x <= rect.right && pos.y >= rect.top && pos.y <= rect.bottom

      const justStartedChecking = safeAreaCheckingRef.current && !wasCheckingRef.current
      wasCheckingRef.current = safeAreaCheckingRef.current

      const debugFill = 'rgba(255,0,0,0.35)'
      const transparentFill = 'rgba(0,0,0,0)'

      if (justStartedChecking) {
        poly.style.fill = showSafeArea ? debugFill : transparentFill
        poly.setAttribute('pointer-events', 'all')
      }

      if (safeAreaCheckingRef.current) {
        const hitEl = document.elementFromPoint(pos.x, pos.y)
        if (isNode(hitEl) && contentRef.current?.contains(hitEl)) {
          safeAreaCheckingRef.current = false
          poly.setAttribute('pointer-events', 'none')
          poly.style.fill = transparentFill
        } else if (isNode(hitEl) && triggerRef.current?.contains(hitEl)) {
          safeAreaCheckingRef.current = false
          poly.setAttribute('pointer-events', 'none')
          poly.style.fill = transparentFill
        } else if (hitEl !== poly) {
          onCloseRef.current()
          return
        }
      }

      if (!safeAreaCheckingRef.current) {
        poly.setAttribute('pointer-events', 'none')

        if (inContent) {
          poly.style.fill = transparentFill
        } else {
          const [b, c] = getSafeAreaCorners(pos, rect)
          poly.style.fill = showSafeArea ? debugFill : transparentFill
          poly.setAttribute('points', `${pos.x},${pos.y} ${b.x},${b.y} ${c.x},${c.y}`)
        }
      }
    },
    [contentRef, triggerRef, safeAreaCheckingRef],
  )

  const handlePolyLeave = React.useCallback(
    (evt: Event) => {
      const poly = polygonRef.current
      if (!safeAreaCheckingRef.current || !poly) {
        return
      }
      const related = (evt as MouseEvent).relatedTarget
      if (isNode(related) && contentRef.current?.contains(related)) {
        safeAreaCheckingRef.current = false
        poly.setAttribute('pointer-events', 'none')
        poly.style.fill = 'rgba(0,0,0,0)'
      } else if (isNode(related) && triggerRef.current?.contains(related)) {
        safeAreaCheckingRef.current = false
        poly.setAttribute('pointer-events', 'none')
        poly.style.fill = 'rgba(0,0,0,0)'
      } else {
        onCloseRef.current()
      }
    },
    [contentRef, triggerRef, safeAreaCheckingRef],
  )

  useDomEvent(useDomEvent.document, 'mousemove', handleMouseMove)
  useDomEvent(polygonRef, 'mouseleave', handlePolyLeave)

  return (
    <RadixPortal className="fixed inset-0 w-full h-full pointer-events-none">
      <svg width="100%" height="100%" className="block overflow-visible">
        <polygon ref={polygonRef} fill="rgba(0,0,0,0)" points="0,0 0,0 0,0" pointerEvents="none" />
      </svg>
    </RadixPortal>
  )
}

function getSafeAreaCorners(
  mouse: { x: number; y: number },
  rect: DOMRect,
): [{ x: number; y: number }, { x: number; y: number }] {
  const cx = (rect.left + rect.right) / 2
  const cy = (rect.top + rect.bottom) / 2
  const dx = Math.abs(mouse.x - cx)
  const dy = Math.abs(mouse.y - cy)

  if (dx >= dy) {
    if (mouse.x < cx) {
      return [
        { x: rect.left, y: rect.top },
        { x: rect.left, y: rect.bottom },
      ]
    }
    return [
      { x: rect.right, y: rect.top },
      { x: rect.right, y: rect.bottom },
    ]
  }
  if (mouse.y < cy) {
    return [
      { x: rect.left, y: rect.top },
      { x: rect.right, y: rect.top },
    ]
  }
  return [
    { x: rect.left, y: rect.bottom },
    { x: rect.right, y: rect.bottom },
  ]
}
